import api from './api';
import { User, LoginCredentials, RegisterData, ApiResponse } from '../types';
import { clearActiveCompanyContext } from '../utils/activeCompany';
import { tokenStorage } from './tokenStorage';

interface AuthResponse {
  user: User;
  token: string;
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage write failures (private mode / quota / restricted envs)
  }
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage remove failures
  }
}

/* ── T316: two days, and the CLIENT knows the number too ──────────────────────────────────────
   The server is the authority — a session row expires and the refresh stops working. This stamp
   exists for the case the server never gets asked: an app left open, or opened offline, must not
   sit there looking signed in three days later. The two numbers are deliberately the same;
   `SESSION_MAX_AGE_MS` mirrors `SESSION_LIFETIME_MS` in backend/src/services/sessionService.js. */
export const SESSION_MAX_AGE_MS = 2 * 24 * 60 * 60 * 1000;
const SESSION_START_KEY = 'forge_session_started_at';

function markSessionStart(): void {
  safeSetItem(SESSION_START_KEY, String(Date.now()));
}

/** Milliseconds this session has been alive, or null when we never stamped one. */
export function sessionAgeMs(): number | null {
  const raw = safeGetItem(SESSION_START_KEY);
  const started = raw ? Number(raw) : NaN;
  if (!Number.isFinite(started)) return null;
  return Date.now() - started;
}

/** True when the session is older than the two days T316 allows. */
export function sessionHasExpired(): boolean {
  const age = sessionAgeMs();
  return age !== null && age >= SESSION_MAX_AGE_MS;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/login', credentials);
    const payload = response.data?.data;
    if (!payload?.user || !payload?.token) {
      throw new Error('Invalid login response from server');
    }
    const { user, token } = payload;
    clearActiveCompanyContext();
    // Routes the auth token through OS-secure storage on desktop, plain
    // localStorage on web (Step 6.3 + Step 13 of the build instructions).
    await tokenStorage.set('token', token, 'local');
    safeSetItem('user', JSON.stringify(user));
    markSessionStart();
    return { user, token };
  },

  /**
   * T314 — sign in with a PIN. It ends in the same stored token as `login`, because the server
   * ends it in the same session; the only difference is what the person typed to get here.
   */
  async loginWithPin(identifier: string, pin: string): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/login-pin', { identifier, pin });
    const payload = response.data?.data;
    if (!payload?.user || !payload?.token) {
      throw new Error('Invalid login response from server');
    }
    const { user, token } = payload;
    clearActiveCompanyContext();
    await tokenStorage.set('token', token, 'local');
    safeSetItem('user', JSON.stringify(user));
    markSessionStart();
    return { user, token };
  },

  /** Whether the signed-in user has a PIN, and whether it is locked out. Never the digits. */
  async getPinStatus(): Promise<{ hasPin: boolean; lockedUntil: string | null }> {
    const response = await api.get<ApiResponse<{ hasPin: boolean; lockedUntil: string | null }>>('/auth/pin');
    return response.data?.data || { hasPin: false, lockedUntil: null };
  },

  /** Set or change it. The CURRENT PASSWORD is the gate — see the service for why. */
  async setPin(currentPassword: string, pin: string): Promise<void> {
    await api.put('/auth/pin', { currentPassword, pin });
  },

  async clearPin(): Promise<void> {
    await api.delete('/auth/pin');
  },

  /** T315 — a platform admin CLEARS someone's PIN. There is no call that SETS one for them. */
  async resetPinFor(userId: string | number): Promise<{ message: string }> {
    const response = await api.post<ApiResponse<{ message: string }>>(`/auth/pin/reset/${userId}`);
    return response.data?.data || { message: 'PIN cleared' };
  },

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/register', data);
    const payload = response.data?.data;
    if (!payload?.user || !payload?.token) {
      throw new Error('Invalid register response from server');
    }
    const { user, token } = payload;
    clearActiveCompanyContext();
    await tokenStorage.set('token', token, 'local');
    safeSetItem('user', JSON.stringify(user));
    return { user, token };
  },

  async getProfile(): Promise<User> {
    const response = await api.get<ApiResponse<User>>('/auth/profile');
    const user = response.data?.data;
    if (!user || typeof user !== 'object') {
      throw new Error('Invalid profile response from server');
    }
    // Keep storage in sync so refreshes pick up latest data
    try {
      if (sessionStorage.getItem('token')) {
        sessionStorage.setItem('user', JSON.stringify(user));
      } else {
        safeSetItem('user', JSON.stringify(user));
      }
    } catch {
      safeSetItem('user', JSON.stringify(user));
    }
    return user;
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/auth/change-password', { currentPassword, newPassword });
  },

  logout(): void {
    clearActiveCompanyContext();
    safeRemoveItem(SESSION_START_KEY);
    // Fire-and-forget on desktop — the in-memory cache clears synchronously
    // so the request interceptor sees null on the next call, and the
    // secure-store delete completes in the background.
    void tokenStorage.remove('token');
    void tokenStorage.remove('platform_admin_token');
    try { sessionStorage.removeItem('user'); } catch {}
    safeRemoveItem('user');
    safeRemoveItem('platform_admin_user');
  },

  getStoredUser(): User | null {
    let userStr: string | null = null;
    try { userStr = sessionStorage.getItem('user'); } catch {}
    if (!userStr) userStr = safeGetItem('user');
    if (!userStr) return null;

    try {
      const parsed = JSON.parse(userStr);
      if (!parsed || typeof parsed !== 'object') {
        safeRemoveItem('user');
        return null;
      }
      return parsed as User;
    } catch {
      // Remove corrupted localStorage value (for example: "undefined")
      safeRemoveItem('user');
      return null;
    }
  },

  /**
   * Synchronous token read. On desktop the returned value comes from the
   * in-memory cache hydrated at boot by tokenStorage.hydrate(); see
   * AuthProvider for the await.
   */
  getToken(): string | null {
    return tokenStorage.get('token');
  },

  isAuthenticated(): boolean {
    return !!tokenStorage.get('token');
  }
};

